/**
 * ประลองยุทธภพ, the weekly tournament's screens (C0-TOURNEY; spec docs/design/ux/2026-10-11-weekly-tournament.md, UX
 * review docs/review/ux/2026-10-11-tourney.md, rules packages/shared/src/systems/tournament.ts). Offline version:
 * - the window (five tabs: สมัคร · คู่รอบนี้ · ตารางคะแนน · แพ้คัดออก · ทำเนียบ), opened by the herald beside the ring, the
 *   game menu (เมนู Esc → ประลองยุทธภพ) or the key 0 (every letter key is taken in client0; the spec's G is the emote menu);
 *   inside: Tab / Shift+Tab switch tabs, Enter presses the tab's main button, Home jumps to the player's row;
 * - the tracker card in the right column between the zone card and the quest (round, next bout, a button);
 * - the bout ribbon over the duel HUD while a tournament bout is fought, and the tournament lines and buttons on the duel
 *   result (no rematch in a tournament bout);
 * - a toast and its own sound when a round is posted, the knock-out starts, the champion is crowned; the crowning
 *   banner for a real week;
 * - the result card, once per event (what was earned, who won), then the holder card when the player won the palm;
 * - the gold plate on the target frame when the target holds the palm.
 * It reads `tourneyOf(world).view()` (and the run's finished rounds for the round chips) and sends `tourney` commands;
 * nothing here decides a rule.
 */
import {
  LEGENDARY,
  TOURNEY,
  TOURNEY_ART,
  TOURNEY_COINS,
  TOURNEY_TEXT,
  WEEKDAY_NAMES,
  duelMatchOf,
  getArt,
  tourneyOf,
  type PlayerCommand,
  type TourneyBoutView,
  type TourneyRow,
  type TourneyView,
  type World,
  type WorldEvent,
} from '@nine/shared';
import { playSfx } from '../sfx';
import { h } from './dom';

type Tab = 'signup' | 'round' | 'standings' | 'bracket' | 'hall';
const TABS: readonly { id: Tab; name: string }[] = [
  { id: 'signup', name: 'สมัคร' },
  { id: 'round', name: 'คู่รอบนี้' },
  { id: 'standings', name: 'ตารางคะแนน' },
  { id: 'bracket', name: 'แพ้คัดออก' },
  { id: 'hall', name: 'ทำเนียบ' },
];

const CSS = `
#tourneyCard{position:relative;width:200px;box-sizing:border-box;margin-top:2px;padding:7px 9px;font-size:12px;line-height:1.35;pointer-events:auto}
#tourneyCard[hidden]{display:none!important}
#tourneyCard b{font-weight:600}
#tourneyCard .tc-h{display:flex;justify-content:space-between;gap:6px;align-items:center}
#tourneyCard .tc-chip{font-size:11px;padding:0 5px;border-radius:4px;background:#b48a2c;color:#241a0a}
#tourneyCard .tc-sub{color:#d4cec3}
#tourneyCard .tc-row{display:flex;gap:4px;margin-top:4px}
#tourneyCard button{flex:1;min-height:28px;cursor:pointer;border:1px solid #d6412f;background:#a3281f;color:#fff;border-radius:5px;font-size:12px}
#tourneyCard button.tc-sec{flex:none;padding:0 7px;background:rgba(22,19,15,.6);border-color:rgba(244,241,234,.3);color:#d4cec3}
#tourneyCard kbd{font-size:10px;padding:0 3px;margin-left:4px;border-radius:3px;background:rgba(0,0,0,.35);color:#f4d08c}
#tourneyCard.tc-hot{border:1px solid #f6c46c}
body.tourney-bout #tourneyCard{display:none!important}
@media (max-width:1180px),(max-height:500px){#tourneyCard{width:168px;padding:4px 6px;font-size:11px}#tourneyCard .tc-more{display:none}}
@media (max-height:500px){body.tc-on #quest .qp{display:none}#tourneyCard .tc-opp{display:none}}
.tourney-ribbon{position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:39;height:22px;line-height:22px;padding:0 14px;border-radius:11px;background:rgba(22,19,15,.88);border:1px solid #b48a2c;color:#f4d08c;font-size:12px;font-weight:600;pointer-events:none;white-space:nowrap}
.tourney-ribbon[hidden]{display:none!important}
body.tourney-bout .duel-top{top:36px!important}
body.tourney-bout .duel-tell{top:108px!important}
body.tourney-bout .duel-result .row:not(.tr-row){display:none}
.duel-result .tr-line{margin:-2px 0 10px;font-size:14px;color:#f4d08c}
.duel-result .tr-row{display:none}
body.tourney-bout .duel-result .tr-row{display:flex}
#tourneyWin{position:fixed;left:50%;transform:translateX(-50%);bottom:calc(var(--hotbar-h,76px) + 76px);z-index:60;width:min(880px,calc(100vw - 460px));height:min(560px,calc(100vh - 280px));display:flex;flex-direction:column;background:#efe6d2;color:#2b2118;border:2px solid #5a3a1c;border-radius:6px;box-shadow:0 10px 40px rgba(0,0,0,.55);font-size:14px}
#tourneyWin[hidden]{display:none!important}
#tourneyWin .tw-head{display:flex;align-items:center;gap:12px;padding:6px 12px;border-bottom:2px solid #5a3a1c;background:#e2d3b3}
#tourneyWin .tw-glyph{font-size:34px;line-height:1;color:#a3281f}
#tourneyWin .tw-title{font-weight:700;font-size:18px}
#tourneyWin .tw-clock{margin-left:auto;font-size:13px;color:#5a3a1c}
#tourneyWin .tw-x{width:30px;height:30px;border:1px solid #5a3a1c;background:#efe6d2;color:#2b2118;cursor:pointer;border-radius:4px;font-size:16px}
#tourneyWin .tw-tabs{display:flex;gap:2px;padding:4px 10px 0;background:#e2d3b3}
#tourneyWin .tw-tabs button{padding:5px 12px;border:1px solid #5a3a1c;border-bottom:0;background:#d6c6a2;color:#2b2118;cursor:pointer;border-radius:5px 5px 0 0;font-size:14px}
#tourneyWin .tw-tabs button.on{background:#efe6d2;font-weight:700;color:#a3281f}
#tourneyWin .tw-body{flex:1;overflow:auto;padding:10px 14px}
#tourneyWin h4{margin:0 0 6px;font-size:15px;color:#a3281f}
#tourneyWin .tw-cols{display:grid;grid-template-columns:1.2fr 1fr;gap:14px}
#tourneyWin .tw-card{border:1px solid #8a6a3c;border-radius:6px;padding:8px 10px;background:#f6eedb;margin-bottom:8px}
#tourneyWin .tw-card.gold{border-color:#b48a2c;border-width:2px}
#tourneyWin .tw-card.seal{border-color:#a3281f;border-width:2px}
#tourneyWin .tw-btn{display:block;width:100%;min-height:40px;margin:4px 0;cursor:pointer;border:1px solid #6e1d15;background:#a3281f;color:#fff;border-radius:5px;font-size:15px;font-weight:600}
#tourneyWin .tw-btn.sec{background:#efe6d2;color:#2b2118;border-color:#5a3a1c;font-weight:400;min-height:32px;font-size:13px}
#tourneyWin .tw-btn[disabled]{opacity:.5;cursor:default}
#tourneyWin :is(button,.tw-btn):focus-visible{outline:2px solid #a3281f;outline-offset:2px}
#tourneyWin ul{margin:4px 0 0 18px;padding:0}
#tourneyWin ol.tw-steps{list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:3px}
#tourneyWin ol.tw-steps li{padding:2px 8px;border:2px solid transparent;border-radius:5px}
#tourneyWin ol.tw-steps li.past{color:#8c7a5c}
#tourneyWin ol.tw-steps li.past::before{content:'✓ '}
#tourneyWin ol.tw-steps li.cur{border-color:#a3281f;color:#a3281f;font-weight:700;background:#fbf4e4}
#tourneyWin table{width:100%;border-collapse:separate;border-spacing:0;font-variant-numeric:tabular-nums}
#tourneyWin th,#tourneyWin td{padding:3px 6px;text-align:left;border-bottom:1px solid #cdbb94;background:#efe6d2}
#tourneyWin th{font-size:12px;color:#5a3a1c;position:sticky;top:0;z-index:2}
#tourneyWin th abbr{text-decoration:none;cursor:help}
#tourneyWin tr.me td{background:#d9c6a0;font-weight:700;position:sticky;top:25px;bottom:0;z-index:1}
#tourneyWin tr.cut td{border-bottom:2px solid #a3281f}
#tourneyWin td.cut-cap{color:#a3281f;font-size:12px;border-bottom:0;padding-top:0}
#tourneyWin .chips{display:flex;gap:2px}
#tourneyWin .chips i{font-style:normal;width:18px;text-align:center;border-radius:3px;font-size:11px;line-height:17px;color:#fff;background:#8c7a5c}
#tourneyWin .chips i.w{background:#1f6b3a}#tourneyWin .chips i.l{background:#a3281f}#tourneyWin .chips i.b{background:#b48a2c;color:#241a0a}
#tourneyWin .tw-tag{font-size:11px;padding:0 5px;border-radius:4px;background:#b48a2c;color:#241a0a;margin-left:4px}
#tourneyWin .tw-muted{color:#6c5a40;font-size:13px}
#tourneyWin .tw-err{color:#a3281f;font-size:13px}
#tourneyWin .tw-pair{display:flex;justify-content:space-between;gap:8px;padding:4px 6px;border-bottom:1px dashed #cdbb94}
#tourneyWin .tw-win{color:#1f6b3a;font-weight:700}
#tourneyWin .tw-vs{display:grid;grid-template-columns:1fr auto 1fr;gap:10px;align-items:center;margin:2px 0 6px}
#tourneyWin .tw-vs .side{display:flex;flex-direction:column}
#tourneyWin .tw-vs .side.r{text-align:right}
#tourneyWin .tw-vs b{font-size:16px}
#tourneyWin .tw-vs .mid{font-size:18px;color:#a3281f;font-weight:700}
#tourneyWin .tw-bar{display:flex;gap:8px;align-items:center;margin-bottom:6px}
#tourneyWin .tw-bracket{display:flex;gap:26px;align-items:center;overflow-x:auto;padding:6px 2px}
#tourneyWin .tw-col{display:flex;flex-direction:column;gap:14px;min-width:210px}
#tourneyWin .tw-bc{border:1px solid #8a6a3c;border-radius:5px;background:#f6eedb;padding:5px 8px;min-height:52px}
#tourneyWin .tw-bc .sd{display:inline-block;min-width:16px;color:#8c7a5c;font-size:12px}
#tourneyWin .tw-bc.dash{border-style:dashed;color:#6c5a40}
#tourneyWin .tw-bc.mine{border-color:#a3281f;border-width:2px}
#tourneyWin .tw-prize{display:flex;flex-direction:column;gap:4px;align-items:center;min-width:120px;color:#5a3a1c;font-size:13px}
#tourneyWin .tw-prize .g{font-size:34px;color:#b48a2c;line-height:1}
#tourneyResult,#tourneyHolder{position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:70;width:min(520px,calc(100vw - 32px));max-height:calc(100vh - 24px);overflow:auto;box-sizing:border-box;background:#efe6d2;color:#2b2118;border:2px solid #b48a2c;border-radius:6px;padding:14px 18px;box-shadow:0 10px 40px rgba(0,0,0,.6);font-size:14px}
#tourneyHolder{width:min(560px,calc(100vw - 32px));border-color:#d9a441;box-shadow:0 0 0 3px rgba(217,164,65,.35),0 10px 40px rgba(0,0,0,.6)}
#tourneyResult[hidden],#tourneyHolder[hidden]{display:none!important}
#tourneyResult h3,#tourneyHolder h3{margin:0 0 8px;font-size:20px;color:#a3281f}
#tourneyHolder h3 .g{font-size:30px;color:#b48a2c;margin-right:8px;vertical-align:-4px}
#tourneyResult .coins{margin:6px 0;padding:6px 10px;background:#f6eedb;border:1px solid #cdbb94;border-radius:5px;font-variant-numeric:tabular-nums}
#tourneyResult .coins div{display:flex;justify-content:space-between}
#tourneyResult .coins .tot{border-top:1px solid #8a6a3c;margin-top:3px;padding-top:3px;font-weight:700}
#tourneyHolder ul{margin:6px 0 6px 18px;padding:0}
#tourneyHolder .chip{display:inline-block;font-size:12px;padding:1px 7px;border-radius:4px;background:#b48a2c;color:#241a0a;margin-right:6px}
#tourneyResult .row,#tourneyHolder .row{display:flex;gap:8px;margin-top:10px}
#tourneyResult button,#tourneyHolder button{min-height:36px;padding:0 18px;cursor:pointer;border:1px solid #6e1d15;background:#a3281f;color:#fff;border-radius:5px;font-size:14px}
#tourneyResult button.sec,#tourneyHolder button.sec{background:#efe6d2;color:#2b2118;border-color:#5a3a1c}
#target.tf-holder{box-shadow:0 0 0 2px #d9a441,0 0 16px rgba(217,164,65,.55)}
#target .tf-plate{display:none;font-size:12px;font-weight:600;color:#241a0a;background:linear-gradient(#f6dc92,#d9a441);border-radius:4px;padding:1px 8px;white-space:nowrap}
#target.tf-holder .tf-plate{display:block}
#target .tf-plate .g{font-family:var(--f-han,serif);margin-right:4px}
#banner.tb-crown{font-size:40px}
@media (max-height:500px){#tourneyWin{height:214px;bottom:calc(var(--hotbar-h,76px) + 8px);width:min(828px,calc(100vw - 16px))}#tourneyWin .tw-head{padding:3px 10px}#tourneyWin .tw-glyph{font-size:30px}#tourneyWin .tw-title{font-size:16px}#tourneyWin .tw-tabs button{font-size:13px;padding:3px 8px}#tourneyWin .tw-body{padding:8px 12px}
#tourneyWin .col-rounds{display:none}#tourneyWin .tw-col{gap:6px;min-width:190px}#tourneyWin .tw-bracket{padding:0 2px;gap:16px}#tourneyWin .tw-bc{min-height:0;padding:2px 8px;font-size:13px;line-height:1.35}#tourneyWin .tw-bc .st{display:none}#tourneyWin .tw-prize .g{font-size:24px}}
@media (pointer:coarse){#tourneyCard button,#tourneyWin .tw-tabs button,#tourneyWin .tw-btn,#tourneyWin .tw-x,#tourneyResult button,#tourneyHolder button,.duel-result .tr-row button{min-height:44px}}
@media (prefers-reduced-motion:no-preference){#tourneyCard.tc-pulse{animation:tcp .5s ease-in-out 2}}
@keyframes tcp{50%{box-shadow:0 0 12px rgba(246,196,108,.9)}}
/* The duel HUD on short screens (UX review m12): the banner sat over the vitals and the long legend ran off both edges.
   CSS only; belongs in ui/duelHud.ts when its owner next edits it. */
@media (max-height:500px){.duel-banner{top:128px;font-size:40px}.duel-banner small{font-size:15px}.duel-legend{display:none}}
`;

const pad = (n: number): string => String(n).padStart(2, '0');
const clockText = (v: TourneyView): string => {
  const hh = Math.floor(v.hour);
  const mm = Math.floor((v.hour - hh) * 60);
  return `วัน${WEEKDAY_NAMES[v.day]} ${pad(hh)}:${pad(mm)} (เวลาในเกม) · สัปดาห์ที่ ${v.week}`;
};
/** A day is an hour of real time: a game hour is 2.5 real minutes. */
const realText = (gameHours: number): string => {
  const m = Math.max(1, Math.round(gameHours * 2.5));
  return m < 60 ? `${m} นาที` : `${Math.floor(m / 60)} ชม.${m % 60 ? ` ${m % 60} นาที` : ''}`;
};
/** The glossary word for the knock-out is แพ้คัดออก. */
const fixWords = (s: string): string => s.replace('รอบคัดออก', 'แพ้คัดออก');
const gateText = (g: NonNullable<TourneyView['nextGate']>): string => `${fixWords(g.label)} · อีกประมาณ ${realText(g.hours)}`;
/** The knock-out's round names: the last round is the final, the one before the semi-finals. */
const koName = (round: number, total: number): string =>
  round === total ? 'นัดชิงชนะเลิศ' : round === total - 1 ? 'รอบรองชนะเลิศ' : `แพ้คัดออก รอบ ${round}`;
/** The offline knock-out is the top 4: two rounds. */
const KO_ROUNDS = 2;
const roundLabel = (label: string): string => {
  const m = /^แพ้คัดออก รอบ (\d+)$/.exec(label);
  return m ? koName(Number(m[1]), KO_ROUNDS) : label;
};
const HOLDER_TAG = `[${TOURNEY_TEXT.holder}]`;
const HOLDER_SEEN = 'ns.tourney.holderSeen';
/** In-game hours from the week start to the knock-out (the holder keeps the palm until the next one). */
const CUT_T = TOURNEY.cutAt.day * 24 + TOURNEY.cutAt.hour;

interface Bout {
  readonly a: number;
  readonly b: number | null;
  readonly w: number | null;
}

export class TourneyUi {
  private win: HTMLElement;
  private body: HTMLElement;
  private tabsEl: HTMLElement;
  private clockEl: HTMLElement;
  private card: HTMLElement;
  private ribbon: HTMLElement;
  private result: HTMLElement;
  private holderCard: HTMLElement;
  private resLine: HTMLElement | null = null;
  private plate: HTMLElement | null = null;
  private tab: Tab = 'signup';
  private opened = false;
  private sig = '';
  private cardSig = '';
  private t = 0;
  private resultFor = '';
  private err = '';
  private bannerTimer = 0;
  private lastTarget = -2;
  private plateSig = '';

  constructor(
    private readonly world: World,
    private readonly send: (c: PlayerCommand) => void,
    private readonly toast: (text: string) => void,
  ) {
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.append(style);

    this.tabsEl = h('div', { class: 'tw-tabs' });
    this.tabsEl.setAttribute('role', 'tablist');
    for (const tb of TABS) {
      const b = h('button', { type: 'button', data: { tab: tb.id } }, tb.name);
      b.setAttribute('role', 'tab');
      b.addEventListener('click', () => this.setTab(tb.id));
      this.tabsEl.append(b);
    }
    this.clockEl = h('span', { class: 'tw-clock' });
    const x = h('button', { type: 'button', class: 'tw-x', title: 'ปิด (Esc)' }, '×');
    x.setAttribute('aria-label', 'ปิด (Esc)');
    x.addEventListener('click', () => this.close());
    this.body = h('div', { class: 'tw-body' });
    this.win = h(
      'section',
      { id: 'tourneyWin', hidden: true },
      h('div', { class: 'tw-head' }, h('span', { class: 'tw-glyph' }, '龍'), h('span', { class: 'tw-title' }, TOURNEY_TEXT.name), this.clockEl, x),
      this.tabsEl,
      this.body,
    );
    this.win.setAttribute('role', 'dialog');
    this.win.setAttribute('aria-label', TOURNEY_TEXT.name);
    this.card = h('section', { id: 'tourneyCard', class: 'g-panel', hidden: true });
    this.card.setAttribute('aria-label', TOURNEY_TEXT.name);
    this.ribbon = h('div', { class: 'tourney-ribbon', hidden: true });
    this.result = h('section', { id: 'tourneyResult', hidden: true });
    this.result.setAttribute('role', 'dialog');
    this.holderCard = h('section', { id: 'tourneyHolder', hidden: true });
    this.holderCard.setAttribute('role', 'dialog');
    document.body.append(this.win, this.ribbon, this.result, this.holderCard);
    const quest = document.getElementById('quest');
    if (quest?.parentElement) quest.parentElement.insertBefore(this.card, quest);
    else document.body.append(this.card);
    this.addMenuEntry();

    addEventListener('keydown', (e) => this.onKey(e), true);
    if (import.meta.env.DEV) (window as unknown as { __tourneyUi?: TourneyUi }).__tourneyUi = this;
  }

  /** เมนู (Esc) → ประลอง group: one more button beside ประลองยุทธ, with its key. */
  private addMenuEntry(): void {
    const arena = document.getElementById('mbArena');
    if (!arena?.parentElement) return;
    const b = h(
      'button',
      { type: 'button', id: 'mbTourney', title: 'ประลองประจำสัปดาห์ ผู้ชนะครองวิชาในตำนาน' },
      h('i', {}, h('span', { class: 'gm-mark' }, '龍')),
      h('span', {}, TOURNEY_TEXT.name),
      h('kbd', {}, '0'),
    );
    b.addEventListener('click', () => this.toggle());
    arena.after(b);
  }

  private onKey(e: KeyboardEvent): void {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // A tournament bout's result: Enter leaves (the duel HUD's Enter would ask for a rematch, which a tournament has not).
    if (e.key === 'Enter' && document.body.classList.contains('tourney-bout') && this.duelResultShown()) {
      this.send({ t: 'duelMatch', op: 'leave' });
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!this.holderCard.hidden) {
      if (e.key === 'Enter' || e.key === 'Escape') this.closeHolder();
      else if (e.key === 'k' || e.key === 'K') this.openArts();
      else return;
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!this.result.hidden && (e.key === 'Enter' || e.key === 'Escape')) {
      this.closeResult();
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (e.key === '0') {
      this.toggle();
      return;
    }
    if (!this.opened) return;
    if (e.key === 'Escape') {
      this.close();
      e.stopPropagation();
    } else if (e.key === 'Tab') {
      const i = TABS.findIndex((t) => t.id === this.tab);
      this.setTab(TABS[(i + (e.shiftKey ? TABS.length - 1 : 1)) % TABS.length]!.id);
      e.preventDefault();
      e.stopImmediatePropagation();
    } else if (e.key === 'Enter') {
      const t = e.target as HTMLElement | null;
      if (t && t.tagName === 'BUTTON' && this.win.contains(t)) return;
      const main = this.body.querySelector<HTMLButtonElement>('.tw-btn:not(.sec):not([disabled])');
      if (main) {
        main.click();
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    } else if (e.key === 'Home') {
      this.body.querySelector('tr.me')?.scrollIntoView({ block: 'center' });
      e.preventDefault();
    }
  }

  get isOpen(): boolean {
    return this.opened;
  }

  open(tab?: Tab): void {
    if (tab) this.tab = tab;
    this.opened = true;
    this.win.hidden = false;
    this.sig = '';
    this.render();
  }

  close(): void {
    this.opened = false;
    this.win.hidden = true;
  }

  toggle(): void {
    if (this.opened) this.close();
    else this.open(this.bestTab());
  }

  private setTab(t: Tab): void {
    this.tab = t;
    this.sig = '';
    this.render();
    this.body.scrollTop = 0;
  }

  /** The tab worth showing first: the bout to fight, the knock-out, else the sign-up. */
  private bestTab(): Tab {
    const v = this.view();
    if (!v) return 'signup';
    if (v.mine) return 'round';
    if (v.stage === 'bracket') return 'bracket';
    if (v.stage === 'none' || v.stage === 'done') return 'signup';
    return this.tab;
  }

  private view(): TourneyView | null {
    return tourneyOf(this.world)?.view() ?? null;
  }

  /** The run's finished Swiss rounds (read only, for the W–L and the round chips). */
  private history(): readonly (readonly Bout[])[] {
    return (this.world.progress.tourney?.run?.history ?? []) as readonly (readonly Bout[])[];
  }

  // ─────────────── events ───────────────

  handle(ev: WorldEvent): void {
    if (ev.type !== 'tourney') return;
    const v = this.view();
    switch (ev.what) {
      case 'open':
        this.open(v?.mine ? 'round' : v?.stage === 'none' || v?.stage === 'done' ? 'signup' : 'standings');
        break;
      case 'signup':
        this.err = '';
        playSfx('tourney.seal');
        break;
      case 'start':
        this.resultFor = '';
        this.err = '';
        playSfx('tourney.gong');
        break;
      case 'round':
        if (v?.mine) {
          this.toast(`${roundLabel(v.mine.label)} · ท่านพบ ${v.mine.opponent}`);
          playSfx('tourney.gong');
          this.card.classList.remove('tc-pulse');
          void this.card.offsetWidth;
          this.card.classList.add('tc-pulse');
        }
        break;
      case 'cut':
        playSfx('tourney.drums');
        break;
      case 'done':
        playSfx('tourney.crown');
        this.crown();
        this.showResult();
        break;
      case 'palm':
        playSfx('tourney.palm');
        break;
      case 'bout':
        break;
    }
    this.sig = '';
    this.cardSig = '';
  }

  // ─────────────── per frame ───────────────

  sync(dt: number): void {
    this.syncPlate();
    this.t += dt;
    if (this.t < 0.25) return;
    this.t = 0;
    const v = this.view();
    const d = duelMatchOf(this.world)?.current;
    const inBout = !!d?.tourney;
    document.body.classList.toggle('tourney-bout', inBout);
    if (inBout && d) {
      this.ribbon.hidden = false;
      const tt = d.tourney!;
      const slot = v && !tt.exhibition && v.stage === 'swiss' ? v.pairs.findIndex((p) => p.mine) : -1;
      const txt = `${TOURNEY_TEXT.name} · ${tt.exhibition ? tt.label : roundLabel(tt.label)}${slot >= 0 ? ` · คู่ที่ ${slot + 1}` : ''}${tt.exhibition ? '' : ' · ลานยุติธรรม'}`;
      if (this.ribbon.textContent !== txt) this.ribbon.textContent = txt;
      if (v) this.syncDuelResult(v, tt.label);
    } else this.ribbon.hidden = true;
    if (!v) return;
    this.syncCard(v);
    if (this.opened) this.render();
  }

  private duelResultShown(): boolean {
    const r = document.querySelector<HTMLElement>('.duel-result');
    return !!r && !r.hidden;
  }

  /** The tournament lines and buttons on the duel HUD's result card (the rematch row is hidden by CSS in a bout). */
  private syncDuelResult(v: TourneyView, label: string): void {
    const res = document.querySelector<HTMLElement>('.duel-result');
    if (!res || res.hidden) return;
    if (!this.resLine || !res.contains(this.resLine)) {
      this.resLine = h('div', { class: 'tr-line' });
      const ok = h('button', { type: 'button' }, 'ตกลง');
      ok.addEventListener('click', () => this.send({ t: 'duelMatch', op: 'leave' }));
      const table = h('button', { type: 'button', class: 'ghost' }, 'ดูตารางคะแนน');
      table.addEventListener('click', () => {
        this.send({ t: 'duelMatch', op: 'leave' });
        this.open('standings');
      });
      const row = h('div', { class: 'row tr-row' }, ok, table);
      const old = res.querySelector('.row');
      res.insertBefore(this.resLine, old);
      res.append(row);
    }
    const next =
      v.stage === 'done'
        ? `จบ${v.mode === 'test' ? 'รอบซ้อม' : TOURNEY_TEXT.name}แล้ว`
        : v.mine && v.mine.label !== label
          ? `ถัดไป: ${roundLabel(v.mine.label)} · พบ ${v.mine.opponent}`
          : v.bye
            ? `รอบถัดไปท่านได้${TOURNEY_TEXT.bye} (+1 แต้ม)`
            : v.inField
              ? 'รอบถัดไป: ดูที่การ์ดด้านขวา'
              : 'ท่านตกรอบแล้ว · ชมผลต่อได้ที่ตารางคะแนน';
    // The score line (set score, foe, round) is the duel HUD's own; this line only says what comes next.
    if (this.resLine.dataset.k === next) return;
    this.resLine.dataset.k = next;
    this.resLine.replaceChildren(next);
  }

  private syncCard(v: TourneyView): void {
    const show = v.stage === 'swiss' || v.stage === 'swissDone' || v.stage === 'bracket' || (v.signed && v.stage !== 'done') || (v.stage === 'none' && v.signed);
    const me = v.standings.find((r) => r.me);
    const sig = JSON.stringify([show, v.stage, v.round, v.mine, v.bye, v.canExhibit, v.signed, v.nextGate && Math.round(v.nextGate.hours * 4), v.mode, v.fighting, v.myPlace, me?.score]);
    if (sig === this.cardSig) return;
    this.cardSig = sig;
    this.card.hidden = !show;
    document.body.classList.toggle('tc-on', show);
    if (!show) return;
    const test = v.mode === 'test';
    const head = h('div', { class: 'tc-h' }, h('b', {}, TOURNEY_TEXT.name), test ? h('span', { class: 'tc-chip' }, 'รอบซ้อม') : null);
    const lines: (HTMLElement | null)[] = [head];
    const tableBtn = (): HTMLElement => {
      const b = h('button', { type: 'button', class: 'tc-sec', title: 'เปิดหน้าต่างประลองยุทธภพ (0)' }, 'ตาราง', h('kbd', {}, '0'));
      b.addEventListener('click', () => this.toggle());
      return b;
    };
    if (v.stage === 'swiss' || v.stage === 'swissDone' || v.stage === 'bracket') {
      const rtxt = v.stage === 'bracket' ? (v.mine ? roundLabel(v.mine.label) : 'รอบแพ้คัดออก') : `รอบสวิส ${v.round}/${v.rounds}`;
      lines.push(h('div', { class: 'tc-sub' }, rtxt, me && v.stage !== 'bracket' ? ` · ${me.score} แต้ม` : ''));
      if (v.mine) {
        lines.push(h('div', { class: 'tc-opp' }, `พบ ${v.mine.opponent}`));
        const b = h('button', { type: 'button' }, 'เริ่มนัด');
        b.addEventListener('click', () => this.send({ t: 'tourney', op: 'fight' }));
        lines.push(h('div', { class: 'tc-row' }, b, tableBtn()));
      } else if (v.bye) {
        lines.push(h('div', {}, `ได้${TOURNEY_TEXT.bye} (+1 แต้ม)`));
        const row = h('div', { class: 'tc-row' });
        if (v.canExhibit) {
          const b = h('button', { type: 'button' }, 'ประลองโชว์');
          b.addEventListener('click', () => this.send({ t: 'tourney', op: 'exhibition' }));
          row.append(b);
        }
        row.append(tableBtn());
        lines.push(row);
      } else {
        lines.push(h('div', { class: 'tc-sub' }, v.inField ? (v.stage === 'bracket' ? 'ท่านตกรอบแล้ว' : 'รอรอบต่อไป') : 'ท่านไม่ได้ลงแข่ง'));
        lines.push(h('div', { class: 'tc-row' }, tableBtn()));
      }
      if (v.nextGate) lines.push(h('div', { class: 'tc-sub tc-more' }, gateText(v.nextGate)));
    } else {
      lines.push(h('div', { class: 'tc-sub' }, 'สมัครแล้ว'));
      if (v.nextGate) lines.push(h('div', { class: 'tc-sub' }, gateText(v.nextGate)));
      lines.push(h('div', { class: 'tc-row' }, tableBtn()));
    }
    this.card.replaceChildren(...lines.filter((x): x is HTMLElement => !!x));
    this.card.classList.toggle('tc-hot', !!v.mine);
  }

  // ─────────────── the holder's target frame ───────────────

  /** A gold frame and plate on the target frame while the target holds the palm. Writes the DOM only on a change. */
  private syncPlate(): void {
    const id = this.world.player.targetId ?? -1;
    const ent = this.world.get(id) as unknown as { title?: string } | undefined;
    const holder = !!ent?.title?.startsWith(HOLDER_TAG);
    const sig = holder ? `${id}|${this.holderLeft()}` : '';
    if (id === this.lastTarget && sig === this.plateSig) return;
    this.lastTarget = id;
    this.plateSig = sig;
    const frame = document.getElementById('target');
    if (!frame) return;
    if (!this.plate) {
      this.plate = h('div', { class: 'tf-plate' });
      frame.querySelector('.tg-head')?.after(this.plate);
    }
    frame.classList.toggle('tf-holder', holder);
    if (holder) this.plate.replaceChildren(h('span', { class: 'g' }, '龍'), `${TOURNEY_TEXT.holder} · ${this.holderLeft()}`);
  }

  /** "เหลือ 6 วัน 22 ชม." of the holder's week (in-game time), from the view's clock. */
  private holderLeft(): string {
    const v = this.view();
    const hd = v?.holder;
    if (!v || !hd) return '';
    const now = (v.week - 1) * 168 + v.day * 24 + v.hour;
    const left = Math.max(0, (hd.week + 1) * 168 + CUT_T - now);
    const d = Math.floor(left / 24);
    const hh = Math.floor(left % 24);
    return `เหลือ ${d ? `${d} วัน ` : ''}${hh} ชม.`;
  }

  // ─────────────── the window ───────────────

  private render(): void {
    const v = this.view();
    if (!v) return;
    const sig = JSON.stringify([this.tab, v, Math.floor(v.hour * 4), this.err]);
    if (sig === this.sig) return;
    this.sig = sig;
    this.clockEl.textContent = clockText(v);
    for (const b of this.tabsEl.querySelectorAll<HTMLButtonElement>('button')) {
      b.classList.toggle('on', b.dataset.tab === this.tab);
      b.setAttribute('aria-selected', String(b.dataset.tab === this.tab));
    }
    const scroll = this.body.scrollTop;
    this.body.replaceChildren(...this.tabBody(v));
    this.body.scrollTop = scroll;
  }

  private opButton(text: string, op: 'signup' | 'withdraw' | 'fight' | 'exhibition' | 'test' | 'abandon', sec = false, disabled = false): HTMLButtonElement {
    const b = h('button', { type: 'button', class: `tw-btn${sec ? ' sec' : ''}`, disabled }, text);
    b.addEventListener('click', () => {
      this.send({ t: 'tourney', op });
      // Sign-up that did not take (a closed window, a locked save): say so under the button, never a modal.
      if (op === 'signup') {
        const v = this.view();
        this.err = v && !v.signed ? 'สมัครไม่สำเร็จ ลองอีกครั้ง' : '';
        this.sig = '';
        this.render();
      }
    });
    return b;
  }

  private tabBody(v: TourneyView): HTMLElement[] {
    switch (this.tab) {
      case 'signup':
        return this.signupTab(v);
      case 'round':
        return this.roundTab(v);
      case 'standings':
        return this.standingsTab(v);
      case 'bracket':
        return this.bracketTab(v);
      case 'hall':
        return this.hallTab(v);
    }
  }

  private signupTab(v: TourneyView): HTMLElement[] {
    const steps = ['สมัคร ถึงเสาร์ 19:30', 'รอบสวิส เสาร์ 20:00 · 5 รอบ', 'แพ้คัดออก 4 คน อาทิตย์ 20:00', 'ผู้ชนะครองวิชา 7 วัน'];
    const cur = v.stage === 'none' ? 0 : v.stage === 'swiss' || v.stage === 'swissDone' ? 1 : v.stage === 'bracket' ? 2 : 3;
    const art = getArt(TOURNEY_ART).name;
    const left = h(
      'div',
      {},
      h(
        'div',
        { class: 'tw-card' },
        h('h4', {}, v.mode === 'test' && v.stage !== 'none' ? 'ขั้นตอน (รอบซ้อม)' : 'ขั้นตอนของสัปดาห์'),
        h('ol', { class: 'tw-steps' }, ...steps.map((s, i) => h('li', { class: i < cur ? 'past' : i === cur ? 'cur' : '' }, s))),
      ),
      h(
        'div',
        { class: 'tw-card rules-card' },
        h('h4', {}, 'กติกา'),
        h(
          'ul',
          {},
          h('li', {}, '32 คน: ท่าน จอมยุทธ์พเนจร 24 คน และคู่ฝึก 7 คน'),
          h('li', {}, 'ทุกนัดชนะ 2 ใน 3 ยก ในลานยุติธรรม ค่าพลังและระดับวิชาเท่ากันหมด ตัดสินที่ฝีมือ'),
          h('li', {}, 'แต้มเท่ากันตัดสินด้วยแต้มคู่แข่ง แล้วดูผลที่เจอกันเอง'),
          h('li', {}, 'รอบที่ไม่มีคู่ได้บาย (+1 แต้ม) และประลองโชว์ได้'),
          h('li', {}, 'ชนะติดกัน 3 สัปดาห์ เป็นปรมาจารย์รับเชิญ พัก 1 สัปดาห์'),
        ),
      ),
      h(
        'div',
        { class: 'tw-card gold rules-card' },
        h('h4', {}, `รางวัล · ${art}`),
        h('div', {}, `${TOURNEY_TEXT.holder}ถือไว้ 7 วัน และต้องป้องกันตำแหน่ง`),
        h('div', { class: 'tw-muted' }, 'สู้กับสัตว์ร้ายและบอสได้พลังเต็ม แต่สู้กับผู้เล่นปรับให้เท่าวิชาธรรมดาขั้นสูงสุด · แลกเปลี่ยนไม่ได้'),
        h('div', { class: 'tw-muted' }, `${TOURNEY_TEXT.coins}ตามแต้ม · ฉายาประจำสัปดาห์ 4 อันดับแรก · ชื่อขึ้น${TOURNEY_TEXT.hall}`),
      ),
    );
    const right: HTMLElement[] = [];
    const status = h('div', { class: 'tw-card seal' }, h('h4', {}, 'สถานะของท่าน'));
    if (v.sittingOut) {
      status.append(h('div', {}, `${TOURNEY_TEXT.guest}: พักการแข่งสัปดาห์นี้`));
    } else if (v.inField && (v.stage === 'swiss' || v.stage === 'swissDone' || v.stage === 'bracket')) {
      status.append(h('div', {}, v.mode === 'test' ? 'กำลังแข่งรอบซ้อม' : 'กำลังแข่งอยู่'));
      if (v.mine) status.append(h('div', { class: 'tw-muted' }, `${roundLabel(v.mine.label)} · พบ ${v.mine.opponent}`), this.opButton('เริ่มนัด', 'fight'));
      status.append(this.opButton('ถอนตัวจากการแข่งที่เหลือ', 'abandon', true));
    } else if (v.signed) {
      status.append(h('div', {}, 'สมัครแล้ว · รอบสวิสเริ่มเสาร์ 20:00 (เวลาในเกม)'));
      if (v.nextGate) status.append(h('div', { class: 'tw-muted' }, gateText(v.nextGate)));
      status.append(this.opButton('ถอนชื่อ', 'withdraw', true));
    } else if (v.signupOpen) {
      status.append(h('div', {}, 'เปิดรับสมัครถึงเสาร์ 19:30'));
      if (v.nextGate) status.append(h('div', { class: 'tw-muted' }, gateText(v.nextGate)));
      status.append(this.opButton('สมัครลงแข่ง', 'signup'));
      if (this.err) status.append(h('div', { class: 'tw-err' }, this.err));
    } else {
      status.append(h('div', {}, 'ปิดรับสมัครแล้ว · สัปดาห์หน้าเปิดสมัครวันจันทร์'));
    }
    if (v.stage === 'done' && v.inField) status.append(h('div', { class: 'tw-win' }, v.myPlace ? `ครั้งล่าสุด: ได้อันดับ ${v.myPlace}` : 'ครั้งล่าสุด: ตกรอบ'));
    right.push(status);
    right.push(
      h(
        'div',
        { class: 'tw-card' },
        h('div', {}, `${TOURNEY_TEXT.coins}: `, h('b', {}, String(v.coins))),
        v.holder ? h('div', {}, `${TOURNEY_TEXT.holder}ตอนนี้: `, h('b', {}, v.holder.name)) : h('div', { class: 'tw-muted' }, `ยังไม่มี${TOURNEY_TEXT.holder}`),
        ...v.titles.slice(-2).map((t) => h('div', { class: 'tw-tag' }, t)),
      ),
    );
    const canTest = !(v.inField && v.mode === 'week' && v.stage !== 'done') && !(v.mode === 'test' && v.stage !== 'done' && v.stage !== 'none');
    right.push(
      h(
        'div',
        { class: 'tw-card' },
        h('div', { class: 'tw-muted' }, 'รอบซ้อม 16 คน 4 รอบ · ไม่มีรางวัลวิชา ไม่ขึ้นทำเนียบ'),
        this.opButton('เริ่มรอบซ้อม', 'test', true, !canTest),
      ),
    );
    return [h('div', { class: 'tw-cols' }, left, h('div', {}, ...right))];
  }

  /** Wins and losses of an entrant over the finished Swiss rounds, and the round chips (ช/พ/บ, oldest first). */
  private record(id: number): { w: number; l: number; chips: ('w' | 'l' | 'b' | '')[] } {
    let w = 0;
    let l = 0;
    const chips: ('w' | 'l' | 'b' | '')[] = [];
    for (const round of this.history()) {
      const r = round.find((x) => x.a === id || x.b === id);
      if (!r) chips.push('');
      else if (r.b === null) chips.push('b');
      else if (r.w === id) {
        w++;
        chips.push('w');
      } else if (r.w !== null) {
        l++;
        chips.push('l');
      } else chips.push('');
    }
    return { w, l, chips };
  }

  private pairRow(p: TourneyBoutView): HTMLElement {
    const win = (id: number | null, name: string | null): HTMLElement => h('span', { class: p.winner === id && id !== null ? 'tw-win' : '' }, name ?? TOURNEY_TEXT.bye);
    const state = p.b === null ? `${TOURNEY_TEXT.bye} · ประลองโชว์` : p.winner !== null ? 'จบ' : 'รอเริ่ม';
    return h('div', { class: 'tw-pair' }, h('span', {}, win(p.aId, p.a), ' พบ ', win(p.bId, p.b)), h('span', { class: 'tw-muted' }, state));
  }

  private roundTab(v: TourneyView): HTMLElement[] {
    if (v.stage === 'none') return [h('div', { class: 'tw-muted' }, `ยังไม่เริ่ม · ${v.nextGate ? gateText(v.nextGate) : ''}`)];
    const out: HTMLElement[] = [];
    const round = v.stage === 'bracket' ? (v.mine ? roundLabel(v.mine.label) : 'รอบแพ้คัดออก') : v.stage === 'done' ? 'จบการแข่ง' : `รอบสวิส ${v.round}/${v.rounds}`;
    const card = h('div', { class: 'tw-card seal' }, h('h4', {}, `${round} · นัดของท่าน`));
    const row = (name: string): TourneyRow | undefined => v.standings.find((r) => r.name === name);
    if (v.mine) {
      const me = v.standings.find((r) => r.me);
      const foe = row(v.mine.opponent);
      const side = (r: TourneyRow | undefined, name: string, right: boolean): HTMLElement => {
        const rec = r ? this.record(r.id) : null;
        const tag = r?.kind === 'wanderer' ? 'พเนจร' : r?.kind === 'spar' ? 'คู่ฝึก' : null;
        return h(
          'div',
          { class: `side${right ? ' r' : ''}` },
          h('b', {}, name, tag ? h('span', { class: 'tw-tag' }, tag) : null),
          h('span', { class: 'tw-muted' }, r ? `${r.score} แต้ม · ชนะ ${rec!.w} แพ้ ${rec!.l} · อันดับ ${r.rank}` : ''),
        );
      };
      card.append(h('div', { class: 'tw-vs' }, side(me, 'ท่าน', false), h('span', { class: 'mid' }, 'พบ'), side(foe, v.mine.opponent, true)), this.opButton('เริ่มนัดที่ลานประลอง', 'fight'));
    } else if (v.bye) {
      card.append(h('div', {}, `รอบนี้ท่านได้${TOURNEY_TEXT.bye} · ได้ 1 แต้มเหมือนชนะ`));
      if (v.canExhibit) card.append(this.opButton('ประลองโชว์', 'exhibition'));
    } else if (!v.inField) card.append(h('div', { class: 'tw-muted' }, 'ท่านไม่ได้ลงแข่ง'));
    else card.append(h('div', { class: 'tw-muted' }, v.stage === 'done' ? 'จบแล้ว' : 'ไม่มีนัดของท่านตอนนี้'));
    out.push(card);
    const pairs = v.stage === 'bracket' ? (v.bracket[v.bracket.length - 1] ?? []) : v.pairs;
    const others = pairs.filter((p) => !p.mine);
    if (others.length) out.push(h('h4', {}, 'คู่อื่น ๆ'), ...others.map((p) => this.pairRow(p)));
    return out;
  }

  private standingsTab(v: TourneyView): HTMLElement[] {
    if (!v.standings.length) return [h('div', { class: 'tw-muted' }, 'ยังไม่เริ่ม')];
    const cut = 4;
    const rounds = this.history().length;
    const bh = h('abbr', { title: 'แต้มรวมของทุกคนที่ท่านเคยเจอ ใช้ตัดสินเมื่อแต้มเท่ากัน' }, 'แต้มคู่แข่ง ⓘ');
    const head = h(
      'tr',
      {},
      h('th', {}, '#'),
      h('th', {}, 'ผู้ลงแข่ง'),
      h('th', {}, 'แต้ม'),
      h('th', {}, 'ชนะ–แพ้'),
      h('th', {}, bh),
      h('th', { class: 'col-rounds' }, 'ผลแต่ละรอบ'),
    );
    const tied = (r: TourneyRow): boolean => v.standings.some((o) => o !== r && o.score === r.score && o.buchholz === r.buchholz);
    const CH = { w: 'ช', l: 'พ', b: 'บ', '': '·' } as const;
    const rows: HTMLElement[] = [];
    for (const r of v.standings) {
      const rec = this.record(r.id);
      rows.push(
        h(
          'tr',
          { class: `${r.me ? 'me' : ''}${r.rank === cut ? ' cut' : ''}` },
          h('td', {}, `${tied(r) ? '=' : ''}${r.rank}`),
          h('td', {}, r.me ? `${r.name} (ท่าน)` : r.name, r.kind === 'wanderer' ? h('span', { class: 'tw-tag' }, 'พเนจร') : r.kind === 'spar' ? h('span', { class: 'tw-tag' }, 'คู่ฝึก') : null),
          h('td', {}, String(r.score)),
          h('td', {}, `${rec.w}–${rec.l}`),
          h('td', {}, String(r.buchholz)),
          h('td', { class: 'col-rounds' }, h('span', { class: 'chips' }, ...rec.chips.map((c) => h('i', { class: c }, CH[c])))),
        ),
      );
      if (r.rank === cut) rows.push(h('tr', {}, h('td', { class: 'cut-cap', colSpan: 6 }, `${cut} อันดับแรกหลังรอบ ${v.rounds} เข้ารอบแพ้คัดออก`)));
    }
    const jump = v.standings.some((r) => r.me)
      ? this.linkBtn('ไปที่ชื่อท่าน (Home)', () => this.body.querySelector('tr.me')?.scrollIntoView({ block: 'center' }))
      : null;
    return [
      h('div', { class: 'tw-bar' }, h('span', { class: 'tw-muted' }, rounds ? `หลังรอบ ${rounds}/${v.rounds}` : 'ยังไม่จบรอบแรก · เรียงตามลำดับ'), jump),
      h('table', {}, h('thead', {}, head), h('tbody', {}, ...rows)),
    ];
  }

  private linkBtn(text: string, fn: () => void): HTMLButtonElement {
    const b = h('button', { type: 'button', class: 'tw-btn sec' }, text);
    b.style.width = 'auto';
    b.style.padding = '0 12px';
    b.style.margin = '0';
    b.addEventListener('click', fn);
    return b;
  }

  private bracketTab(v: TourneyView): HTMLElement[] {
    const prize = h(
      'div',
      { class: 'tw-prize' },
      h('span', { class: 'g' }, '龍'),
      h('span', {}, v.mode === 'test' ? 'รอบซ้อม ไม่มีรางวัลวิชา' : getArt(TOURNEY_ART).name),
      v.mode !== 'test' && v.holder ? h('span', {}, `${TOURNEY_TEXT.holder}: ${v.holder.name}`) : null,
    );
    if (!v.bracket.length) {
      return [
        h('div', { class: 'tw-muted' }, 'เริ่มหลังรอบสวิสจบ · อาทิตย์ 20:00'),
        h(
          'div',
          { class: 'tw-bracket' },
          h('div', { class: 'tw-col' }, h('div', { class: 'tw-muted' }, 'รอบรองชนะเลิศ'), h('div', { class: 'tw-bc dash' }, 'อันดับ 1 พบ 4'), h('div', { class: 'tw-bc dash' }, 'อันดับ 2 พบ 3')),
          h('div', { class: 'tw-col' }, h('div', { class: 'tw-muted' }, 'นัดชิงชนะเลิศ'), h('div', { class: 'tw-bc dash' }, 'ผู้ชนะทั้งสองคู่')),
          prize,
        ),
      ];
    }
    const seed = (name: string | null): string => {
      const r = name ? v.standings.find((x) => x.name === name) : undefined;
      return r ? String(r.rank) : '';
    };
    const cols = v.bracket.map((round, i) =>
      h(
        'div',
        { class: 'tw-col' },
        h('div', { class: 'tw-muted' }, koName(i + 1, Math.max(KO_ROUNDS, v.bracket.length))),
        ...round.map((p) =>
          h(
            'div',
            { class: `tw-bc${p.mine ? ' mine' : ''}` },
            h('div', { class: p.winner === p.aId ? 'tw-win' : '' }, h('span', { class: 'sd' }, seed(p.a)), p.a),
            h('div', { class: p.winner === p.bId && p.bId !== null ? 'tw-win' : '' }, h('span', { class: 'sd' }, seed(p.b)), p.b ?? TOURNEY_TEXT.bye),
            h('div', { class: 'tw-muted st' }, p.winner === null ? 'รอเริ่ม' : 'จบ'),
          ),
        ),
      ),
    );
    const out: HTMLElement[] = [h('div', { class: 'tw-bracket' }, ...cols, prize)];
    if (v.places.length) {
      const champ = v.mode === 'test' ? 'ผู้ชนะรอบซ้อม' : TOURNEY_TEXT.holder;
      out.push(h('div', { class: 'tw-card gold' }, h('h4', {}, `${champ}: ${v.places[0]}`), h('div', {}, `รองชนะเลิศ: ${v.places[1] ?? '-'}`)));
    }
    return out;
  }

  private hallTab(v: TourneyView): HTMLElement[] {
    const out: HTMLElement[] = [];
    if (v.holder) {
      out.push(
        h(
          'div',
          { class: 'tw-card gold' },
          h('h4', {}, `${TOURNEY_TEXT.holder}ตอนนี้: ${v.holder.name}`),
          h('div', {}, `${getArt(TOURNEY_ART).name} · ${this.holderLeft()} (เวลาในเกม)`),
          h('div', {}, `ชนะติดกัน ${v.holder.streak}/3 สัปดาห์${v.holder.who === 'player' ? ' · ท่านถือวิชานี้อยู่' : ''}`),
        ),
      );
    }
    if (!v.hall.length) {
      out.push(h('div', { class: 'tw-muted' }, `ยังไม่มี${TOURNEY_TEXT.holder} · ประลองยุทธภพครั้งแรก เสาร์นี้ 20:00`));
      return out;
    }
    const head = h('tr', {}, ...['สัปดาห์', TOURNEY_TEXT.holder, 'รอบสวิส', 'ติดกัน'].map((t) => h('th', {}, t)));
    const rows = [...v.hall].reverse().map((e) =>
      h(
        'tr',
        {},
        h('td', {}, String(e.week)),
        h('td', {}, e.name, e.streak >= 3 ? h('span', { class: 'tw-tag' }, TOURNEY_TEXT.guest) : null),
        h('td', {}, `ชนะ ${e.wins}`),
        h('td', {}, e.streak > 1 ? h('span', { class: 'tw-tag' }, `ติดกัน ×${e.streak}`) : '×1'),
      ),
    );
    out.push(h('table', {}, h('thead', {}, head), h('tbody', {}, ...rows)));
    return out;
  }

  // ─────────────── the result, the crowning, the holder card ───────────────

  /** A real week's crowning: the gold banner for 4 s (the system's chat line names the champion too). */
  private crown(): void {
    const v = this.view();
    if (!v || v.mode !== 'week' || !v.places[0]) return;
    const b = document.getElementById('banner');
    if (!b) return;
    const text = `龍 ${v.places[0]} ครอง${getArt(TOURNEY_ART).name}`;
    b.textContent = text;
    b.classList.add('show', 'tb-crown');
    clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => {
      if (b.textContent === text) b.classList.remove('show');
      b.classList.remove('tb-crown');
    }, 4000);
  }

  private showResult(): void {
    const v = this.view();
    if (!v || v.stage !== 'done') return;
    const key = `${v.mode}:${v.week}:${v.places.join(',')}`;
    if (key === this.resultFor) return;
    this.resultFor = key;
    const test = v.mode === 'test';
    const lines: HTMLElement[] = [];
    lines.push(h('h3', {}, test ? 'จบรอบซ้อม' : `จบ${TOURNEY_TEXT.name}`));
    lines.push(h('div', {}, `${test ? 'ผู้ชนะรอบซ้อม' : TOURNEY_TEXT.holder}: `, h('b', {}, v.places[0] ?? '-')));
    const me = v.standings.find((r) => r.me);
    if (v.inField) {
      const swiss = me ? `จบรอบสวิส อันดับ ${me.rank} จาก ${v.standings.length}` : '';
      lines.push(h('div', {}, v.myPlace ? `ท่านได้อันดับ ${v.myPlace}` : 'ท่านไม่ได้เข้ารอบแพ้คัดออก', swiss ? h('span', { class: 'tw-muted' }, ` · ${swiss}`) : ''));
    }
    if (!test && v.inField) {
      const wins = me?.score ?? 0;
      const place = v.myPlace ? (TOURNEY_COINS.place[v.myPlace - 1] ?? 0) : 0;
      const parts: [string, number][] = [
        ['ลงแข่ง', TOURNEY_COINS.entry],
        [`ชนะรอบสวิส ${wins} ครั้ง`, wins * TOURNEY_COINS.swissWin],
      ];
      if (place) parts.push([`อันดับ ${v.myPlace}`, place]);
      const total = parts.reduce((s, [, n]) => s + n, 0);
      lines.push(
        h(
          'div',
          { class: 'coins' },
          ...parts.map(([t, n]) => h('div', {}, h('span', {}, t), h('span', {}, `+${n}`))),
          h('div', { class: 'tot' }, h('span', {}, `${TOURNEY_TEXT.coins}รวม`), h('span', {}, `+${total}`)),
        ),
      );
      if (v.myPlace && v.titles.length) lines.push(h('div', {}, 'ฉายาใหม่: ', h('b', {}, v.titles[v.titles.length - 1]!)));
    }
    if (test) lines.push(h('div', { class: 'tw-muted' }, 'รอบซ้อมไม่มีรางวัลวิชา ไม่ขึ้นทำเนียบ'));
    const ok = h('button', { type: 'button' }, 'ตกลง');
    ok.addEventListener('click', () => this.closeResult());
    const table = h('button', { type: 'button', class: 'sec' }, 'ดูสายแพ้คัดออก');
    table.addEventListener('click', () => {
      this.closeResult();
      this.open('bracket');
    });
    lines.push(h('div', { class: 'row' }, ok, table));
    this.result.replaceChildren(...lines);
    this.result.hidden = false;
    ok.focus();
  }

  private closeResult(): void {
    this.result.hidden = true;
    this.maybeHolderCard();
  }

  /** The champion's card, once per won week: what the palm does, for how long, and the defence. */
  private maybeHolderCard(): void {
    const v = this.view();
    const hd = v?.holder;
    if (!v || !hd || hd.who !== 'player') return;
    const seen = Number(localStorage.getItem(HOLDER_SEEN) ?? -1);
    if (seen === hd.week) return;
    localStorage.setItem(HOLDER_SEEN, String(hd.week));
    this.showHolderCard();
  }

  /** Public for the review captures (dev only, `window.__tourneyUi`). */
  showHolderCard(): void {
    const v = this.view();
    const art = getArt(TOURNEY_ART).name;
    const streak = v?.holder?.streak ?? 1;
    const ok = h('button', { type: 'button' }, 'รับทราบ');
    ok.addEventListener('click', () => this.closeHolder());
    const arts = h('button', { type: 'button', class: 'sec' }, 'ดูวิชา (K)');
    arts.addEventListener('click', () => this.openArts());
    this.holderCard.replaceChildren(
      h('h3', {}, h('span', { class: 'g' }, '龍'), `ท่านคือ${TOURNEY_TEXT.holder}`),
      h('div', {}, 'ท่านได้ถือ ', h('b', {}, art), ' หนึ่งสัปดาห์'),
      h(
        'ul',
        {},
        h('li', {}, `สู้กับสัตว์ร้าย โจร และบอส: พลังเต็ม (×${LEGENDARY.vsOthers} ของวิชาธรรมดาขั้นสูงสุด)`),
        h('li', {}, 'สู้กับผู้เล่น: พลังเท่าวิชาธรรมดาขั้นสูงสุด ตัดสินที่ฝีมือ'),
        h('li', {}, 'แลกเปลี่ยนหรือขายไม่ได้ · ทุกคนเห็นตรามังกรบนกรอบเป้าหมายของท่าน'),
        h('li', {}, `ต้องป้องกันตำแหน่งในประลองยุทธภพครั้งหน้า · ${this.holderLeft()} (เวลาในเกม)`),
      ),
      h('div', {}, h('span', { class: 'chip' }, 'ถือครอง 7 วัน'), h('span', { class: 'chip' }, `ชนะติดกัน ${streak}/3`)),
      h('div', { class: 'row' }, ok, arts),
    );
    this.holderCard.hidden = false;
    ok.focus();
  }

  private closeHolder(): void {
    this.holderCard.hidden = true;
  }

  private openArts(): void {
    this.closeHolder();
    document.getElementById('mbArts')?.click();
  }
}
